import type { MerchantOpeningTableVO } from "@/api/business/types/table"
import type { OrderDetailVO } from "@/api/user/types/member"
import type { BuildOrderTableChargePrintDetailsOptions } from "@/utils/tableChargePrintDetails"
import { hasAuthoritativeTableBillingSegments } from "@/utils/tableChargePrintDetails"
import { calculateTableFeeList } from "@/views/page/business/hooks/math"

type OpeningTableInfoResponse = {
  code?: number
  data?: MerchantOpeningTableVO
}

export type ResolveReceiptTableChargeOptionsParams = {
  fetchOpeningTableInfoByRecord: (recordId: number) => Promise<OpeningTableInfoResponse>
  preResolvedOptions?: BuildOrderTableChargePrintDetailsOptions
}

export function hasReceiptTableChargeOptions(options?: BuildOrderTableChargePrintDetailsOptions) {
  return Boolean(
    options?.tableFeeList?.length
    || hasAuthoritativeTableBillingSegments(options?.billingSegments),
  )
}

export function buildReceiptTableChargeOptions(
  data?: MerchantOpeningTableVO,
): BuildOrderTableChargePrintDetailsOptions {
  if (hasAuthoritativeTableBillingSegments(data?.billingSegments)) {
    return { billingSegments: data!.billingSegments }
  }
  if (!data?.tableConsumeInfoList?.length) {
    return {}
  }
  return {
    tableFeeList: calculateTableFeeList(data.tableConsumeInfoList, data.tableDiscountInfo, true),
    pauseRecordList: data.pauseRecordList,
  }
}

export async function resolveReceiptTableChargeOptions(
  data: OrderDetailVO,
  params: ResolveReceiptTableChargeOptionsParams,
): Promise<BuildOrderTableChargePrintDetailsOptions> {
  if (hasReceiptTableChargeOptions(params.preResolvedOptions)) {
    return params.preResolvedOptions!
  }

  const tableRecordId = resolveReceiptTableRecordId(data)
  if (!tableRecordId) {
    return {}
  }

  const res = await params.fetchOpeningTableInfoByRecord(tableRecordId)
  if (res.code !== 0) {
    return {}
  }
  return buildReceiptTableChargeOptions(res.data)
}

function resolveReceiptTableRecordId(data: OrderDetailVO) {
  const orderTableRecordId = normalizeRecordId(data.orderInfo?.tableRecordId)
  if (orderTableRecordId) {
    return orderTableRecordId
  }
  return normalizeRecordId((data.tableRecordInfo?.[0] as { tableRecordId?: unknown } | undefined)?.tableRecordId)
}

function normalizeRecordId(value: unknown) {
  const recordId = Number(value)
  return Number.isFinite(recordId) && recordId > 0 ? recordId : undefined
}
